//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { SortDir } from '@apis/campaign-requests/shared/Sorting';
import { SamplingCampaignQueryErrorAlert } from '@campaigns/components/shared/SamplingCampaignQueryErrorAlert';
import { PrepaidPurchaseOptionsTable } from '@campaigns/components/shared/prepaid-purchase-options/PrepaidPurchaseOptionsTable';
import { PurchaseOptionsProvider } from '@campaigns/components/shared/purchase-options/PurchaseOptionsContext';
import { PurchaseOptionsPagination } from '@campaigns/components/shared/purchase-options/PurchaseOptionsPagination';
import { PurchaseOptionsTableControls } from '@campaigns/components/shared/purchase-options/PurchaseOptionsTableControls';
import { PurchaseOptionsSortKey } from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { BodyText, SectionText } from '@components/atoms/text/Text';

interface ChangePurchaseOptionModalTableProps {
  headerText: string;
  currentPage: number;
  error?: Error | null;
  isLoading: boolean;
  itemsPerPage: number;
  showCreateNewPurchaseButton?: boolean;
  onCreateNewPurchase?: () => void;
  onSelectionChange: (id: string) => void;
  onSortChange: (key: PurchaseOptionsSortKey) => void;
  purchaseOptions: PurchaseOption[];
  resultsText: string;
  searchTerm: string;
  selectedOptionId?: string;
  setCurrentPage: (n: number) => void;
  setItemsPerPage: (n: number) => void;
  setSearchTerm: (s: string) => void;
  sortDir: SortDir;
  sortKey: PurchaseOptionsSortKey;
  totalItems: number;
  expiredPurchaseOptionModalTitle?: string;
}

export const ChangePurchaseOptionModalTable = ({
  currentPage,
  error,
  expiredPurchaseOptionModalTitle,
  headerText,
  isLoading,
  itemsPerPage,
  onCreateNewPurchase,
  onSelectionChange,
  onSortChange,
  purchaseOptions,
  resultsText,
  searchTerm,
  selectedOptionId,
  setCurrentPage,
  setItemsPerPage,
  setSearchTerm,
  showCreateNewPurchaseButton,
  sortDir,
  sortKey,
  totalItems,
}: ChangePurchaseOptionModalTableProps) => {
  if (isLoading) {
    return <CenteredSpinner />;
  }

  if (error) {
    return <SamplingCampaignQueryErrorAlert error={error} />;
  }

  return (
    <ContentWrapper>
      {expiredPurchaseOptionModalTitle && (
        <SectionText>{expiredPurchaseOptionModalTitle}</SectionText>
      )}
      <BodyText>{headerText}</BodyText>

      <TableContainer>
        <ControlsWrapper>
          <PurchaseOptionsTableControls
            resultsText={resultsText}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
          {showCreateNewPurchaseButton && (
            <PrimaryLinkButton onClick={onCreateNewPurchase}>
              Create a New Purchase
            </PrimaryLinkButton>
          )}
        </ControlsWrapper>
        <PurchaseOptionsProvider readOnly={false}>
          <PrepaidPurchaseOptionsTable
            onSelectionChange={onSelectionChange}
            onSortChange={onSortChange}
            purchaseOptions={purchaseOptions}
            selectedOptionId={selectedOptionId}
            sortDir={sortDir}
            sortKey={sortKey}
          />
        </PurchaseOptionsProvider>
        {totalItems > 0 && (
          <PurchaseOptionsPagination
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            setCurrentPage={setCurrentPage}
            setItemsPerPage={setItemsPerPage}
            totalItems={totalItems}
          />
        )}
      </TableContainer>
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[2]};
`,
);

const ControlsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[2]};
`,
);
